<template>
  <div class="boss-main-container">
    <div class="boss-main-content-wrap">
      <applicant-detail-previous-duplicate
        v-if="recentDuplicatedEntry.entryId"
        :job="recentDuplicatedEntry.job"
        :entry-id="recentDuplicatedEntry.entryId"
        :entry-date="recentDuplicatedEntry.entryDate"
        data-test="duplicated-info"
      />
      <section>
        <div class="boss-layout-block margin-bottom-0">
          <router-link
            :to="{ name: 'applicantTop' }"
            class="boss-back-to-list-link"
          >
            応募者一覧
          </router-link>
        </div>
        <div class="boss-layout-block margin-bottom-1">
          <div class="boss-title-wrap">
            <div class="boss-layout boss-layout-gap-between">
              <div>
                <h1
                  class="boss-title-2"
                  data-test="label-job"
                >
                  求人ID: {{ job.id }}
                  <br>{{ job.name }}
                </h1>
              </div>
              <div
                class="is-boss-only-pc"
              >
                <button
                  class="boss-button"
                  data-test="show-entry-offer-history-button"
                  @click="showEntryOfferHistory"
                >
                  <span class="button-before boss-icon-triangle" />
                  <span>応募時の原稿を確認する</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="boss-layout-block margin-bottom-0">
          <div class="boss-layout boss-layout-gap-between">
            <div>
              <div
                class="boss-applicant-name-kana"
                data-test="label-phonetic"
              >
                {{ applicant.phonetic }}
              </div>
              <div class="boss-layout boss-layout-start boss-layout-baseline boss-layout-gap-small">
                <div>
                  <div
                    class="boss-applicant-name"
                    data-test="label-name"
                  >
                    {{ applicant.name }}
                  </div>
                </div>
                <div data-test="label-user-id">
                  （ID:{{ applicant.id }}）
                </div>
                <div>
                  <span
                    v-if="applicant.isDuplicatedEntries"
                    v-tooltip="{ content: '別職種での応募や、過去1年以内の掲載時に応募があった場合に表示されます。'}"
                    class="boss-status-label"
                    data-test="label-duplicated-entries"
                  >重複応募</span>
                  <span
                    v-if="entryFormType"
                    class="boss-status-label"
                    data-test="label-entry-form"
                  >{{ entryFormType }}</span>
                  <span
                    v-if="pathwayLabel"
                    class="boss-status-label"
                    data-test="label-pathway"
                  >{{ pathwayLabel }}</span>
                </div>
              </div>
            </div>
            <div>
              <div class="boss-layout boss-layout-gap-small boss-layout-vertical boss-layout-end">
                <div
                  class="boss-text"
                  data-test="label-responsible-staff"
                >
                  人事担当者: {{ responsibleStaffName | parseHyphen }}
                </div>
                <div>
                  <div class="boss-bonus-check-group">
                    <span
                      v-for="(item, i) in specialFavors"
                      :key="i"
                      v-tooltip="{ content: specialFavorBy(item.id).description}"
                      :data-test="'label-special-favor-'+ i"
                      class="boss-bonus-check-text boss-check-true"
                    >{{ item.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="boss-layout-block margin-bottom-0">
          <table class="boss-table-4">
            <!-- 話を聞きたい応募（カジュアル面談）の場合 -->
            <tr v-if="applicant.isCasualInterviewRequest">
              <th>話を聞きたい応募<br>（カジュアル面談）<br>送信内容</th>
              <td data-test="text-casual-interview-result">
                【1】面談の都合がつきやすい日時および時間帯：{{ casualInterviewHopeDates | parseEnqueteResult }} <br>
                【2】面談の方法について：{{ casualInterviewHopeWays | parseEnqueteResult }} <br>
                【3】直近の経験職種・経験年数：{{ primaryUserOldJobAndExperienceYearsLabel }}
              </td>
            </tr>
            <!-- 選考応募の場合 -->
            <tr v-else>
              <th>応募時点の面接希望日（最大5件）</th>
              <td data-test="text-hope-interview-dates">
                {{ hopeInterviewDates | parseDates }}
              </td>
            </tr>
          </table>
        </div>
      </section>
    </div>
    <applicant-detail-selection
      :entry-id="Number($route.params.entryId)"
    />
    <!-- カジュアル面談応募の場合の注意文言 -->
    <div
      v-if="applicant.isCasualInterviewRequest"
      class="pega-casual-interview-caution-message-container"
      data-test="casual-interview-caution-message-container"
    >
      <div
        class="pega-casual-interview-caution-message-title"
        data-test="casual-interview-caution-message-title"
      >
        この応募は「<span class="red-title">話を聞きたい応募（カジュアル面談）</span>」です
      </div>
      <div data-test="casual-interview-caution-message">
        選考応募とは異なるのでご注意ください。この応募者との面談を実施しない場合は原則返信不要です。
      </div>
    </div>
    <div class="boss-main-content-wrap">
      <div class="boss-layout-block">
        <div>
          <div class="boss-layout-block margin-bottom-1">
            <ul
              id="boss-tab-menu"
              data-tabs
              class="tabs boss-tabs-menu-list"
            >
              <li
                :class="{'is-active': currentTabKey === 'resume'}"
                class="tabs-title"
              >
                <a
                  class="boss-tabs-link"
                  data-test="link-resume"
                  @click="changeCurrent('resume')"
                >
                  <svg
                    class="boss-tabs-link-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <path
                        d="M12.5 2.5H7.5H3.75H1.25C0.55625 2.5 0 3.05625 0 3.75V16.25C0 16.9438 0.55625 17.5 1.25 17.5H18.75C19.4438 17.5 20 16.9438 20 16.25V3.75C20 3.05625 19.4438 2.5 18.75 2.5H16.25H12.5ZM16.25 3.75H18.125C18.4688 3.75 18.75 4.03125 18.75 4.375V15.625C18.75 15.9688 18.4688 16.25 18.125 16.25H1.875C1.53125 16.25 1.25 15.9688 1.25 15.625V4.375C1.25 4.03125 1.53125 3.75 1.875 3.75H3.75H7.5H12.5H16.25Z"
                        fill="#113063"
                      />
                      <path
                        d="M13.125 9.375H14.375C14.7188 9.375 15 9.09375 15 8.75C15 8.40625 14.7188 8.125 14.375 8.125H13.125C12.7812 8.125 12.5 8.40625 12.5 8.75C12.5 9.09375 12.7812 9.375 13.125 9.375Z"
                        fill="#113063"
                      />
                      <path
                        d="M15.625 10.625H13.125C12.7812 10.625 12.5 10.9062 12.5 11.25C12.5 11.5938 12.7812 11.875 13.125 11.875H15.625C15.9688 11.875 16.25 11.5938 16.25 11.25C16.25 10.9062 15.9688 10.625 15.625 10.625Z"
                        fill="#113063"
                      />
                      <path
                        d="M8.27492 10.825C8.93742 10.375 9.37492 9.6125 9.37492 8.75C9.37492 7.36875 8.25618 6.25 6.87492 6.25C5.49367 6.25 4.37492 7.36875 4.37492 8.75C4.37492 9.6125 4.81243 10.375 5.47493 10.825C3.69993 11.3687 3.18118 12.85 3.15618 12.9313C3.04993 13.2563 3.22492 13.6125 3.54992 13.7188C3.88117 13.825 4.23117 13.65 4.33742 13.325C4.34992 13.3062 4.84992 11.875 6.87492 11.875C8.86867 11.875 9.38742 13.2687 9.40617 13.325C9.49367 13.5875 9.73742 13.75 9.99992 13.75C10.0624 13.75 10.1312 13.7375 10.1999 13.7188C10.5249 13.6125 10.7062 13.2563 10.5937 12.9313C10.5687 12.85 10.0499 11.3687 8.27492 10.825ZM6.87492 7.5C7.56242 7.5 8.12492 8.0625 8.12492 8.75C8.12492 9.4375 7.56242 10 6.87492 10C6.18742 10 5.62492 9.4375 5.62492 8.75C5.62492 8.0625 6.18742 7.5 6.87492 7.5Z"
                        fill="#113063"
                      />
                    </g>

                  </svg>
                  レジュメ
                </a>
              </li>
              <li
                :class="{'is-active': currentTabKey === 'message'}"
                class="tabs-title"
              >
                <a
                  class="boss-tabs-link"
                  data-test="link-message"
                  @click="changeCurrent('message')"
                >
                  <svg
                    class="boss-tabs-link-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.75 0H5C4.30625 0 3.75 0.55625 3.75 1.25V3.75H1.25C0.55625 3.75 0 4.30625 0 5V13.75C0 14.4438 0.55625 15 1.25 15H3.75V18.75C3.75 19.2563 4.05625 19.7125 4.51875 19.9062C4.675 19.9688 4.8375 20 5 20C5.325 20 5.64375 19.875 5.88125 19.6313L10.5188 15H15C15.6938 15 16.25 14.4438 16.25 13.75V11.25H18.75C19.4438 11.25 20 10.6938 20 10V1.25C20 0.55625 19.4438 0 18.75 0ZM15 13.125C15 13.4688 14.7188 13.75 14.375 13.75C14.375 13.75 11.0375 13.75 10.5188 13.75C10 13.75 9.6375 14.1187 9.6375 14.1187L5 18.75V14.375C5 14.0312 4.71875 13.75 4.375 13.75H1.875C1.53125 13.75 1.25 13.4688 1.25 13.125V5.625C1.25 5.28125 1.53125 5 1.875 5H14.375C14.7188 5 15 5.28125 15 5.625V13.125ZM18.75 9.375C18.75 9.71875 18.4688 10 18.125 10H16.25V5C16.25 4.30625 15.6938 3.75 15 3.75H5V1.875C5 1.53125 5.28125 1.25 5.625 1.25H18.125C18.4688 1.25 18.75 1.53125 18.75 1.875V9.375Z"
                      fill="#113063"
                    />
                    <path
                      d="M4.375 8.75H7.5C7.84375 8.75 8.125 8.46875 8.125 8.125C8.125 7.78125 7.84375 7.5 7.5 7.5H4.375C4.03125 7.5 3.75 7.78125 3.75 8.125C3.75 8.46875 4.03125 8.75 4.375 8.75Z"
                      fill="#113063"
                    />
                    <path
                      d="M11.875 10H4.375C4.03125 10 3.75 10.2812 3.75 10.625C3.75 10.9688 4.03125 11.25 4.375 11.25H11.875C12.2188 11.25 12.5 10.9688 12.5 10.625C12.5 10.2812 12.2188 10 11.875 10Z"
                      fill="#113063"
                    />
                  </svg>
                  メッセージ
                </a>
              </li>
              <li
                :class="{'is-active': currentTabKey === 'selection'}"
                class="tabs-title"
              >
                <a
                  class="boss-tabs-link"
                  data-test="link-selection"
                  @click="changeCurrent('selection')"
                >
                  <svg
                    class="boss-tabs-link-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.45 16.8L16.1125 13.4625C16.9875 12.1 17.5 10.4875 17.5 8.75C17.5 3.91875 13.5813 0 8.75 0C3.91875 0 0 3.91875 0 8.75C0 13.5813 3.91875 17.5 8.75 17.5C10.4875 17.5 12.1 16.9875 13.4625 16.1125L16.8 19.45C17.5312 20.1812 18.7188 20.1812 19.45 19.45C20.1812 18.7188 20.1812 17.5312 19.45 16.8ZM8.75 16.25C4.60625 16.25 1.25 12.8938 1.25 8.75C1.25 4.60625 4.60625 1.25 8.75 1.25C12.8938 1.25 16.25 4.60625 16.25 8.75C16.25 12.8938 12.8938 16.25 8.75 16.25ZM18.5687 18.5687C18.325 18.8125 17.9312 18.8125 17.6875 18.5687L14.4812 15.3625C14.7937 15.0875 15.0938 14.7937 15.3625 14.4812L18.5687 17.6875C18.8125 17.925 18.8125 18.325 18.5687 18.5687Z"
                      fill="#113063"
                    />
                    <g clip-path="url(#clip0)">
                      <path
                        d="M13.6436 12.1562C13.5873 12.0625 12.1686 10 8.74984 10C5.33109 10 3.91234 12.0625 3.85609 12.1562C3.66234 12.4437 3.74359 12.8313 4.03109 13.025C4.31859 13.2188 4.70609 13.1375 4.89984 12.85C4.90609 12.8313 5.99359 11.25 8.74984 11.25C11.4686 11.25 12.5623 12.7875 12.6061 12.8438C12.7248 13.025 12.9248 13.125 13.1248 13.125C13.2436 13.125 13.3623 13.0938 13.4686 13.0187C13.7561 12.8312 13.8373 12.4437 13.6436 12.1562Z"
                        fill="#113063"
                      />
                      <path
                        d="M8.75 9.375C10.1312 9.375 11.25 8.25625 11.25 6.875V5.625C11.25 4.24375 10.1312 3.125 8.75 3.125C7.36875 3.125 6.25 4.24375 6.25 5.625V6.875C6.25 8.25625 7.36875 9.375 8.75 9.375ZM7.5 5.625C7.5 4.9375 8.0625 4.375 8.75 4.375C9.44375 4.375 10 4.9375 10 5.625V6.875C10 7.5625 9.44375 8.125 8.75 8.125C8.0625 8.125 7.5 7.5625 7.5 6.875V5.625Z"
                        fill="#113063"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0">
                        <rect
                          width="10"
                          height="10"
                          fill="white"
                          transform="translate(3.75 3.125)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  選考
                </a>
              </li>
            </ul>
          </div>
          <div class="boss-layout-block">
            <keep-alive>
              <component
                :is="tabs[currentTabKey]"
                :entry-id="entryId"
                :entry-company-name="entryCompanyName"
                :is-withdraw="isWithdraw"
                data-test="tab-content"
              />
            </keep-alive>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.boss-layout-block {
  margin-bottom: 50px;
}
</style>
<script>
import ApplicantResume from '@/components/organisms/ApplicantResume'
import ApplicantMessage from '@/components/organisms/ApplicantMessage'
import ApplicantSelection from '@/components/organisms/ApplicantSelection'
import ApplicantDetailSelection from '@/components/organisms/ApplicantDetailSelection'
import ApplicantDetailPreviousDuplicate from '@/components/organisms/ApplicantDetailPreviousDuplicate'
import _ from 'lodash'
import { createDate } from '@/lib/dateUtils.js'
import { mapGetters } from 'vuex'
import { directTypeLabel } from '@/lib/labelValueMappings'

export default {
  components: {
    ApplicantDetailSelection,
    ApplicantDetailPreviousDuplicate,
    ApplicantResume,
    ApplicantMessage,
    ApplicantSelection
  },
  filters: {
    parseDates (val) {
      return val || 'なし'
    },
    parseHyphen (val) {
      return val || '―'
    },
    parseEnqueteResult (val) {
      return val || '未回答'
    }
  },
  props: {
    tabKey: {
      type: String,
      default: 'resume'
    }
  },
  data () {
    const newVar = {
      job: {
        id: '',
        name: ''
      },
      entryCompanyName: '',
      applicant: {
        id: '',
        name: '',
        phonetic: '',
        isDuplicatedEntries: false,
        hopeInterviewDates: [],
        entryDate: '',
        user: {},
        pathway: null,
        isCasualInterviewRequest: false
      },
      responsibleStaffName: '',
      entryFormType: '',
      specialFavors: [],
      recentDuplicatedEntry: {
        entryId: null,
        entryDate: '',
        job: {
          id: null,
          name: ''
        }
      },
      currentTabKey: this.tabKey,
      tabs: {
        resume: 'applicant-resume',
        message: 'applicant-message',
        selection: 'applicant-selection'
      },
      entryId: Number(this.$route.params.entryId),
      casualInterviewEnqueteResults: {
        hopeDate: [],
        hopeWay: [],
        primaryUserOldJobLabel: '',
        primaryUserOldJobExperienceYearsLabel: ''
      }
    }
    return newVar
  },
  computed: {
    ...mapGetters({
      specialFavorBy: 'specialFavorBy'
    }),
    hopeInterviewDates () {
      return _.map(_.take(this.applicant.hopeInterviewDates, 5), o => {
        const isSameDay = createDate(o.from)?.isSame(o.to, 'day')
        return `${createDate(o.from)?.format('YYYY/MM/DD HH:mm')} 〜 ${(isSameDay) ? createDate(o.to)?.format('HH:mm') : createDate(o.to)?.format('YYYY/MM/DD HH:mm')}`
      }).join('、')
    },
    isWithdraw () {
      return _.get(this.applicant, 'user.isWithdrawal', false)
    },
    pathwayLabel () {
      return _.get(directTypeLabel, this.applicant.pathway, '')
    },
    casualInterviewHopeDates () {
      return _.map(this.casualInterviewEnqueteResults.hopeDate, element => {
        return element
      }).join('、')
    },
    casualInterviewHopeWays () {
      return _.map(this.casualInterviewEnqueteResults.hopeWay, element => {
        return element
      }).join('、')
    },
    primaryUserOldJobAndExperienceYearsLabel () {
      return `${this.casualInterviewEnqueteResults.primaryUserOldJobLabel} ${this.casualInterviewEnqueteResults.primaryUserOldJobExperienceYearsLabel} `
    }
  },
  watch: {
    $route: {
      async handler (val) {
        this.fetchEntry().then(() => this.applicant.isCasualInterviewRequest && this.fetchCasualInterviewEnqueteResult())
        this.fetchHopeInterviewDate()
        this.$set(this, 'entryId', Number(val.params.entryId))
      }
    }
  },
  async created () {
    this.fetchEntry().then(() => this.applicant.isCasualInterviewRequest && this.fetchCasualInterviewEnqueteResult())
    this.fetchHopeInterviewDate()
  },
  methods: {
    fetchEntry () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}`).then(response => {
        this.$set(this, 'job', _.get(response.data, 'job', { id: '', name: '' }))
        this.$set(this, 'entryCompanyName', _.get(response.data, 'companyName', ''))
        this.$set(this.applicant, 'phonetic', _(response.data).at(['user.lastnameKana', 'user.firstnameKana']).join(' '))
        this.$set(this.applicant, 'name', _(response.data).at(['user.lastname', 'user.firstname']).join(' '))
        this.$set(this.applicant, 'id', _.get(response.data, 'user.id', ''))
        this.$set(this, 'entryFormType', _.get(response.data, 'entryFormType', ''))
        this.$set(this, 'responsibleStaffName', _.get(response.data, 'responsibleStaffName', ''))
        this.$set(this, 'specialFavors', _.get(response.data, 'specialFavors', []))
        this.$set(this.applicant, 'entryDate', _.get(response.data, 'entryDate', ''))
        this.$set(this.applicant, 'isDuplicatedEntries', _.get(response.data, 'isDuplicatedEntries', false))
        this.$set(this.applicant, 'pathway', _.get(response.data, 'pathway', null))
        this.$set(this.recentDuplicatedEntry, 'job', _.get(response.data, 'recentDuplicatedEntry.job', { id: null, name: '' }))
        this.$set(this.recentDuplicatedEntry, 'entryId', _.get(response.data, 'recentDuplicatedEntry.entryId', null))
        this.$set(this.recentDuplicatedEntry, 'entryDate', _.get(response.data, 'recentDuplicatedEntry.entryDate', ''))
        this.$set(this.applicant, 'user', _.get(response.data, 'user', {}))
        this.$set(this.applicant, 'isCasualInterviewRequest', _.get(response.data, 'isCasualInterviewRequest', {}))
      })
    },
    fetchHopeInterviewDate () {
      this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/hope_interview_dates`).then(response => {
        this.$set(this.applicant, 'hopeInterviewDates', response.data)
      })
    },
    changeCurrent (key) {
      this.$set(this, 'currentTabKey', key)
    },
    showEntryOfferHistory () {
      this.$http.secured.post(`/entry/view/entries/${this.$route.params.entryId}/job_history_url`).then(response => {
        window.open(response.data)
      })
    },
    // カジュアル面談アンケート結果を取得
    fetchCasualInterviewEnqueteResult () {
      this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/casual_interview_enquete_result`).then(response => {
        this.$set(this, 'casualInterviewEnqueteResults', response.data)
      })
    }
  }
}
</script>
